import type { BlockData, ConnectedReviewData, TestimonialsBlockProps } from '../types';
import { theme, sectionColors, cssColor, cardGridClass, cardTextColor } from '../theme';

function stars(rating?: number): string | null {
  if (typeof rating !== 'number' || !Number.isFinite(rating)) return null;
  const n = Math.max(0, Math.min(5, Math.round(rating)));
  if (n === 0) return null;
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

const SOURCE_LABEL: Record<ConnectedReviewData['source'], string> = { google: 'Google', zillow: 'Zillow', realsatisfied: 'RealSatisfied' };

/** One card as the grid draws it — a typed testimonial or a connected review. */
interface Card {
  quote: string;
  author: string;
  authorUrl?: string | null;
  location?: string;
  rating?: number | null;
  /** Connected reviews only: where it came from, and when. */
  source?: ConnectedReviewData['source'];
  when?: string | null;
}

function shortDate(iso: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : null;
}

/**
 * Pure testimonials grid — client reviews as quote cards. Static content; a
 * responsive grid (server-safe, no carousel JS). Renders nothing when empty.
 *
 * `reviewSource` adds the agent's connected Google / Zillow reviews, which the host reads live and
 * passes in `reviews` (never stored in the block). Each one names its author and source, and each
 * source gets a "See all on …" line — what Google's terms ask of a site showing its reviews.
 */
export function TestimonialsBlock(props: TestimonialsBlockProps & { reviews?: BlockData['reviews'] }) {
  const { heading, testimonials = [], reviewSource = 'manual', reviews } = props;
  const colors = sectionColors(props, { bg: theme.surface, text: theme.text });

  const typedAll: Card[] = Array.isArray(testimonials) ? testimonials : [];
  // Google shares 5 and Zillow 10; six keeps the grid to two rows.
  const max = 6;
  const connected: Card[] = reviewSource === 'manual'
    ? []
    : (reviews?.reviews ?? []).slice(0, max).map((r) => ({
        quote: r.text,
        author: r.author,
        authorUrl: r.authorUrl,
        rating: r.rating,
        source: r.source,
        when: r.relativeDate ?? shortDate(r.date),
      }));
  const sources = reviewSource === 'manual' ? [] : (reviews?.sources ?? []);
  // "Connected only" with nothing connected (not set up yet, or a source unreadable right now)
  // falls back to the typed testimonials, so the section never vanishes from the live site.
  const typed = reviewSource === 'connected' && connected.length > 0 ? [] : typedAll;
  const cards = [...typed, ...connected];
  if (cards.length === 0) return null;

  // "Cards per row" (when set) wins over the count-based default.
  const grid = cardGridClass(props,
    cards.length === 1
      ? 'grid-cols-1 sm:grid-cols-1'
      : cards.length === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3');
  const starColor = cssColor(props.starColor) ?? theme.primary;
  // White unless Card colour is set; the quote's words stay readable on it (theme.ts cardTextColor).
  const cardBg = cssColor(props.cardBgColor) ?? '#ffffff';
  const cardLook = { backgroundColor: cardBg, color: cardTextColor(props, { cardBg, customCard: Boolean(cssColor(props.cardBgColor)) }) };

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-6xl">
        {heading ? (
          <h2
            className="mb-8 text-center text-2xl font-semibold"
            style={{ fontFamily: theme.headingFont }}
          >
            {heading}
          </h2>
        ) : null}
        <div className={`grid gap-6 ${grid}`}>
          {cards.map((t, i) => {
            const rated = stars(t.rating ?? undefined);
            return (
              <figure
                key={i}
                className="blk-card flex flex-col rounded-lg border border-black/10 p-6"
                style={cardLook}
              >
                {rated ? (
                  <div className="mb-3 text-sm tracking-wide" style={{ color: starColor }}>
                    {rated}
                  </div>
                ) : null}
                <blockquote className="flex-1 text-sm leading-relaxed">“{t.quote}”</blockquote>
                <figcaption className="mt-4 text-sm font-semibold">
                  {t.authorUrl ? (
                    <a href={t.authorUrl} target="_blank" rel="noopener noreferrer nofollow" className="hover:underline">
                      {t.author}
                    </a>
                  ) : (
                    t.author
                  )}
                  {t.location ? (
                    <span className="font-normal opacity-60"> · {t.location}</span>
                  ) : null}
                  {t.source ? (
                    <span className="mt-1 block text-xs font-normal opacity-60">
                      {`${SOURCE_LABEL[t.source]} review${t.when ? ` · ${t.when}` : ''}`}
                    </span>
                  ) : null}
                </figcaption>
              </figure>
            );
          })}
        </div>
        {sources.length > 0 ? (
          <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm">
            {sources.map((s) => (
              <p key={s.source} className="opacity-80">
                {typeof s.rating === 'number' ? (
                  <span className="font-semibold" style={{ color: starColor }}>{`${s.rating.toFixed(1)} ★`}</span>
                ) : null}
                {typeof s.count === 'number' ? ` from ${s.count} ${SOURCE_LABEL[s.source]} reviews` : ` ${SOURCE_LABEL[s.source]} reviews`}
                {s.profileUrl ? (
                  <>
                    {' · '}
                    <a href={s.profileUrl} target="_blank" rel="noopener noreferrer" className="underline">
                      {`See all on ${SOURCE_LABEL[s.source]}`}
                    </a>
                  </>
                ) : null}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
